import { Link } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { stageLabel } from '../lib/format';
import { Badge, Card, Empty, PageHeader } from '../ui/kit';
import { C3PageLoading } from '../ui/C3Loader';

type B = { id: string; name: string; short_code: string | null; stage: string; pc_date: string | null; revised_pc_date: string | null };

export default function Home() {
  const { site, siteId, profile, access } = useApp();
  const buildings = useRows<B>(
    () =>
      supabase
        .from('buildings')
        .select('id, name, short_code, stage, pc_date, revised_pc_date')
        .eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000')
        .is('deleted_at', null)
        .order('sort'),
    [siteId],
    ['buildings'],
  );
  const contracts = useRows<{ id: string }>(
    () => supabase.from('contracts').select('id').eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000').is('deleted_at', null),
    [siteId],
    ['contracts'],
  );

  if (!access?.super_admin && !access?.roles.length) {
    return (
      <>
        <PageHeader title="Welcome" />
        <Card>
          <Empty>Your account is set up, but no role has been given to you yet. Ask the Super Admin to add you to a client/site.</Empty>
        </Card>
      </>
    );
  }

  const active = buildings.rows.filter((b) => !['complete'].includes(b.stage));
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Pacific/Fiji' });

  return (
    <>
      <PageHeader
        title="Action Centre"
        sub={`${today} · ${site?.name ?? 'No site selected'} · ${active.length} active buildings`}
      />

      <Card title="Needs action">
        <Empty>
          Claims, variations, RFIs and notices that are waiting on the PM team will appear here from V0.2, once the Smartsheet
          registers are imported. Hello{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''} — for now, check the
          buildings, organisations and contracts below and correct anything that's wrong.
        </Empty>
      </Card>

      <div className="kpis">
        <div className="kpi">
          <span>Buildings</span>
          <b>{buildings.rows.length}</b>
        </div>
        <div className="kpi">
          <span>Active</span>
          <b>{active.length}</b>
        </div>
        <div className="kpi">
          <span>Contracts</span>
          <b>{contracts.rows.length}</b>
        </div>
      </div>

      <Card title="Buildings & projects" actions={<Link to="/buildings" style={{ color: '#f2a66e', fontWeight: 700, fontSize: 12 }}>Open list</Link>}>
        {buildings.loading ? (
          <C3PageLoading label="Loading the Action Centre" />
        ) : buildings.rows.length === 0 ? (
          <Empty>No buildings for this site yet.</Empty>
        ) : (
          <div className="grid">
            {buildings.rows.map((b) => (
              <Link key={b.id} to={`/buildings?open=${b.id}`} className="tile" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <h3>{b.name}</h3>
                  <Badge tone={b.stage === 'construction' ? 'orange' : b.stage === 'complete' ? 'good' : 'neutral'}>{stageLabel(b.stage)}</Badge>
                </div>
                <span className="muted small">
                  {b.short_code ?? '—'}
                  {b.revised_pc_date ? ` · revised PC ${b.revised_pc_date}` : b.pc_date ? ` · PC ${b.pc_date}` : ''}
                </span>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
